import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StudentImportAnalysis, StudentImportCommitResult } from '@repo/shared-types';
import type { ImportAnalyzeOverride, ImportCommitInput } from '@repo/validation';
import { fetchApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useUpgradeDialogStore } from '../stores/upgradeDialogStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface AnalyzeVars {
  file: File;
  override?: ImportAnalyzeOverride;
}

/** Shape of a server guard's error body on the raw (non-fetchApi) multipart
 * request below — mirrors GuardErrorBody in lib/api.ts. */
interface GuardErrorBody {
  code?: string;
  message?: string;
  feature?: string;
}

/**
 * Analyzes an uploaded student roster (.xlsx/.csv). Posts multipart directly
 * (not via fetchApi, which forces `Content-Type: application/json`) so the
 * browser sets the multipart boundary itself. Optionally takes an `override`
 * (the user-corrected column mapping) so the server skips its own AI
 * column-mapping call.
 */
export function useAnalyzeStudentImport() {
  return useMutation<StudentImportAnalysis, Error, AnalyzeVars>({
    mutationFn: async ({ file, override }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (override) formData.append('override', JSON.stringify(override));

      const token = useAuthStore.getState().accessToken;
      const res = await fetch(`${API_BASE}/students/import/analyze`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const json = await res.json();
      if (!json.success) {
        const err = json.error as GuardErrorBody | undefined;
        // Defense in depth: the Import button is already locked for orgs
        // without aiImport, but surface the upgrade dialog here too in case
        // this call is ever reached some other way (e.g. a stale mapping
        // override retried after a downgrade).
        if (err?.code === 'FEATURE_LOCKED') {
          useUpgradeDialogStore.getState().open(err.feature);
        }
        throw new Error(err?.message ?? 'Không thể phân tích file');
      }
      return json.data as StudentImportAnalysis;
    },
  });
}

/** Commits the reviewed row set: creates students + payers. Invalidates the
 * students list on success so the page reflects the newly-imported rows. */
export function useCommitStudentImport() {
  const qc = useQueryClient();
  return useMutation<StudentImportCommitResult, Error, ImportCommitInput>({
    mutationFn: (data) =>
      fetchApi<StudentImportCommitResult>('/students/import/commit', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
    },
  });
}
